/**
 * Data layer for `public-trust-center-with-auditor-attestation`. Thin typed fetch wrappers over /api/v1/public-trust-center-with-auditor-attestation.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface PublicTrustCenterWithAuditorAttestationRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListPublicTrustCenterWithAuditorAttestationResponse {
	count: number;
	items: PublicTrustCenterWithAuditorAttestationRow[];
}

export interface CreatePublicTrustCenterWithAuditorAttestationBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/public-trust-center-with-auditor-attestation";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPublicTrustCenterWithAuditorAttestationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPublicTrustCenterWithAuditorAttestationResponse>(res);
}

export async function create(body: CreatePublicTrustCenterWithAuditorAttestationBody): Promise<PublicTrustCenterWithAuditorAttestationRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<PublicTrustCenterWithAuditorAttestationRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const publicTrustCenterWithAuditorAttestationQueryKeys = {
	all: ["public-trust-center-with-auditor-attestation"] as const,
	list: () => [...publicTrustCenterWithAuditorAttestationQueryKeys.all] as const,
	detail: (id: string) => [...publicTrustCenterWithAuditorAttestationQueryKeys.all, id] as const,
};
